'use client';

import * as TogglePrimitive from '@radix-ui/react-toggle';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { Icon } from '@phosphor-icons/react';
import { cn } from '@utils/cn';
import { LucideIcon } from 'lucide-react';
import { Wobble } from './wobble';

const toggleVariantsClasses = cva(
  'relative inline-flex items-center justify-center shrink-0 rounded-xl text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground',
  {
    variants: {
      variant: {
        default: 'bg-transparent hover:bg-muted hover:text-muted-foreground',
        outline:
          'border border-input bg-transparent hover:bg-accent/0 hover:text-accent-foreground',
        solid:
          'border border-input bg-transparent data-[state=on]:bg-primary data-[state=on]:text-primary-foreground',
        destructive: 'bg-transparent hover:bg-destructive hover:text-destructive',
      },
      size: {
        default: 'h-10 px-3',
        sm: 'h-9 px-2.5',
        lg: 'h-11 px-5',
        fit: 'p-2 leading-none whitespace-nowrap',
        card: 'p-4 flex-col gap-2 items-start justify-start text-start',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> &
    VariantProps<typeof toggleVariantsClasses>
>(({ className, variant, size, ...props }, ref) => (
  <Wobble ref={ref}>
    <TogglePrimitive.Root
      ref={ref}
      className={cn(toggleVariantsClasses({ variant, size, className }))}
      {...props}
    />
  </Wobble>
));
Toggle.displayName = TogglePrimitive.Root.displayName;

const ToggleIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement> & { Icon: Icon | LucideIcon }
>(({ className, Icon, ...props }, ref) => (
  <div ref={ref} className={cn('mb-2', className)} {...props}>
    <Icon height="initial" width="initial" className="size-8" />
  </div>
));
ToggleIcon.displayName = 'ToggleIcon';

const ToggleTitle = React.forwardRef<HTMLHeadingElement, React.HTMLProps<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-base font-semibold tracking-tight', className)} {...props} />
  ),
);
ToggleTitle.displayName = 'ToggleTitle';

const ToggleDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLProps<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
ToggleDescription.displayName = 'ToggleDescription';

export {
  Toggle,
  ToggleIcon,
  ToggleTitle,
  ToggleDescription,
  toggleVariantsClasses as toggleVariants,
};
